/**
 * Cliente HTTP das três PWAs.
 *
 * O access token vive só em memória — nunca em localStorage. XSS que
 * consiga ler localStorage leva a sessão inteira; em memória, some ao
 * fechar a aba. A sessão longa é o refresh token, que está num cookie
 * HttpOnly que o JavaScript não enxerga.
 *
 * A API é servida no mesmo host da PWA (/api), então não há CORS nem
 * cookie cruzando subdomínio.
 */

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

/**
 * Uma renovação por vez.
 *
 * Sem isso, uma tela que dispara cinco requisições ao abrir faria cinco
 * refresh em paralelo. Como o refresh token é rotativo e de uso único, o
 * segundo em diante seria tratado como reuso — e o servidor derrubaria a
 * sessão inteira, corretamente.
 */
let refreshing: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken?: string };
      if (!data.accessToken) return false;
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Libera na próxima volta do event loop para que chamadas que já
      // estavam esperando reusem este resultado.
      queueMicrotask(() => {
        refreshing = null;
      });
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** FormData vai cru, sem content-type manual — o browser põe o boundary */
  formData?: FormData;
  signal?: AbortSignal;
  /** não tenta renovar sessão (usado pelo próprio login) */
  skipRefresh?: boolean;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (options.body !== undefined) headers['content-type'] = 'application/json';

    return fetch(path.startsWith('/') ? path : `/api/${path}`, {
      method: options.method ?? (options.body || options.formData ? 'POST' : 'GET'),
      headers,
      credentials: 'include',
      signal: options.signal,
      body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    });
  };

  let res = await send();

  if (res.status === 401 && !options.skipRefresh) {
    if (await refreshSession()) {
      res = await send();
    } else {
      accessToken = null;
      onUnauthorized?.();
    }
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message =
      (parsed as { error?: string })?.error ?? `erro ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}
